import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './WorkReport.css';
import { userService, workReportService } from '../services/api';

const monthNames = ['Január','Február','Marec','Apríl','Máj','Jún','Júl','August','September','Október','November','December'];



const WorkReport = ({ user }) => {
  const params = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState('');

  const year = useMemo(() => parseInt(params.year || String(new Date().getFullYear()), 10), [params.year]);
  const month = useMemo(() => parseInt(params.month || String(new Date().getMonth()+1), 10), [params.month]);

  const monthLabel = `${monthNames[month-1] || ''} ${year}`;

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setLoading(true);
        setError('');
        // Load users for admin/supervisor, else only self
        if (user.role === 'admin') {
          const all = await userService.getAll();
          setUsers(all);
          setSelectedUserId(prev => prev || (user.id));
        } else if (user.role === 'supervisor') {
          const all = await userService.getAll();
          const filtered = all.filter(u => u.project_id === user.project_id);
          setUsers(filtered);
          setSelectedUserId(prev => prev || (user.id));
        } else {
          setUsers([user]);
          setSelectedUserId(user.id);
        }
      } catch (e) {
        setError('Nepodarilo sa načítať používateľov');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!selectedUserId) return;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const data = await workReportService.getMonthly(selectedUserId, year, month);
        const rows = (data.weeks || []).map(w => {
          const s = new Date(w.start);
          const e = new Date(w.end);
          return {
            week: w.week,
            period: `${s.toLocaleDateString('sk-SK')} – ${e.toLocaleDateString('sk-SK')}`,
            hours: w.hours,
            amount: w.amount,
            tasks: w.tasks || []
          };
        });
        setEntries(rows);
      } catch (e) {
        setError('Nepodarilo sa načítať pracovný výkaz');
        setEntries([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedUserId, year, month]);

  const onChangeMonth = (delta) => {
    const d = new Date(year, month-1+delta, 1);
    const ny = d.getFullYear();
    const nm = d.getMonth()+1;
    navigate(`/work-report/${ny}/${String(nm).padStart(2,'0')}`);
  };

  return (
    <div className="work-report-container">
      <div className="wr-header">
        <h1>🧾 Pracovný výkaz</h1>
        <div className="wr-period">
          <button onClick={()=>onChangeMonth(-1)}>{'‹'}</button>
          <span>{monthLabel}</span>
          <button onClick={()=>onChangeMonth(1)}>{'›'}</button>
        </div>
        <div className="wr-user-select">
          <label>Osoba:&nbsp;</label>
          <select value={selectedUserId || ''} onChange={(e)=>setSelectedUserId(Number(e.target.value))}>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className="wr-loading">Načítavam...</div>}
      {error && !loading && <div className="wr-error">❌ {error}</div>}

      {!loading && !error && (
        <table className="wr-table">
          <thead>
            <tr>
              <th>Týždeň</th>
              <th>Obdobie</th>
              <th>Hodiny</th>
              <th>Úlohy (projekt štandardné vety)</th>
              <th>Suma</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={5} style={{textAlign:'center', color:'#6c757d'}}>Žiadne záznamy</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.week}>
                <td>KW {e.week}</td>
                <td>{e.period}</td>
                <td>{e.hours}</td>
                <td>{e.tasks && e.tasks.length ? e.tasks.join(', ') : <span style={{color:'#6c757d'}}>—</span>}</td>
                <td>{e.amount != null ? new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' }).format(e.amount) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="wr-note">
        Poznámka: zoznam „Štandardných prác“ na projekte a výpočet sumy podľa sadzieb bude doplnený v ďalšom kroku.
      </div>
    </div>
  );
};

export default WorkReport;
